import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  MenuItem,
  Card,
  CardContent,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  Radio,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import axiosInstance from '../AxiosInstance';
import { useParams, useNavigate } from 'react-router-dom';

const ADD_NEW_SUPPLIER_VALUE = '__add_new_supplier__';
const EDIT_UNIT_DRAFT_KEY = 'edit_unit_draft';
const EDIT_UNIT_LAST_SUPPLIER_KEY = 'last_created_supplier_id_edit_unit';

const EditUnit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State variables for the unit details
  const [product_id, setProductId] = useState('');
  const [unit_type, setUnitType] = useState(''); // Changed variable name to be more general
  const [unit_category, setUnitCategory] = useState('');
  const [opposite_unit_id, setOppositeUnitId] = useState(''); // Store the selected opposite unit
  const [products, setProducts] = useState([]);
  const [existingUnits, setExistingUnits] = useState([]); // For dropdown of existing units
  const [conversionRate, setConversionRate] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(true);
  // Suppliers (multi-select + primary)
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]); // number[] as strings in UI
  const [primarySupplierId, setPrimarySupplierId] = useState('');
  const [supplierError, setSupplierError] = useState('');
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);

  // Track the original product_id for this unit so we don't clear comparison on initial load
  const originalProductIdRef = useRef(null);

  // Fetch the data for the product list and unit details
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Fetch products and unit details in parallel
        const [productsResponse, unitResponse] = await Promise.all([
          axiosInstance.get('/products'),
          axiosInstance.get(`/units/${id}`)
        ]);

        const unit = unitResponse.data;
        console.log('Fetched unit details:', unit);

        // Set products first
        setProducts(productsResponse.data);

        if (unit && unit.product_id) {
          // Remember the original product for this unit
          originalProductIdRef.current = unit.product_id;

          // Fetch existing units for the specific product
          const unitsResponse = await axiosInstance.get(`/units/product/${unit.product_id}`);
          setExistingUnits(unitsResponse.data);

          // Now set all the form values after we have all the data
          setProductId(unit.product_id || '');
          setUnitType(unit.unit_type || '');
          setUnitCategory(unit.unit_category || '');
          setOppositeUnitId(unit.opposite_unit_id || '');
          setConversionRate(unit.conversion_factor || '');
          // Prefill suppliers and primary if present
          if (Array.isArray(unit.supplier_ids)) {
            setSelectedSupplierIds(unit.supplier_ids.map(String));
          }
          if (unit.supplier_id) {
            setPrimarySupplierId(String(unit.supplier_id));
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbarMessage('Error fetching unit details');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  // Fetch user's suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await axiosInstance.get('/suppliers');
        const raw = res.data || [];
        const nonMarket = raw.filter((s) => !s.market_name);
        setSuppliers(nonMarket);
      } catch (e) {
        console.warn('Failed to fetch suppliers for EditUnit:', e);
      }
    };
    fetchSuppliers();
  }, []);

  // Keep primary valid within selected set
  useEffect(() => {
    if (primarySupplierId && !selectedSupplierIds.includes(primarySupplierId)) {
      // Auto-clear or auto-promote to first selected
      setPrimarySupplierId(selectedSupplierIds[0] || '');
    }
  }, [selectedSupplierIds, primarySupplierId]);

  // Restore draft and newly created supplier when returning from Add Supplier
  useEffect(() => {
    if (loading) return;

    const draftJson = sessionStorage.getItem(EDIT_UNIT_DRAFT_KEY);
    if (!draftJson) {
      return;
    }

    try {
      const draft = JSON.parse(draftJson);

      setProductId(draft.product_id || '');
      setUnitType(draft.unit_type || '');
      setUnitCategory(draft.unit_category || '');
      setOppositeUnitId(draft.opposite_unit_id || '');
      setConversionRate(draft.conversionRate || '');

      const baseSelected = draft.selectedSupplierIds || [];
      const newSupplierId = sessionStorage.getItem(EDIT_UNIT_LAST_SUPPLIER_KEY);

      if (newSupplierId) {
        const merged = baseSelected.includes(newSupplierId)
          ? baseSelected
          : [...baseSelected, newSupplierId];
        setSelectedSupplierIds(merged);
        setPrimarySupplierId(newSupplierId);
      } else {
        setSelectedSupplierIds(baseSelected);
        setPrimarySupplierId(draft.primarySupplierId || '');
      }
    } catch (e) {
      console.warn('Failed to restore EditUnit draft from sessionStorage', e);
    } finally {
      sessionStorage.removeItem(EDIT_UNIT_DRAFT_KEY);
      sessionStorage.removeItem(EDIT_UNIT_LAST_SUPPLIER_KEY);
    }
  }, [loading]);

  // Fetch existing units when product changes (for when user manually changes product)
  useEffect(() => {
    if (product_id && !loading) {
      const fetchExistingUnits = async () => {
        try {
          const response = await axiosInstance.get(`/units/product/${product_id}`);
          setExistingUnits(response.data);
          // Reset opposite unit selection only if the product has changed from the original
          if (originalProductIdRef.current && product_id !== originalProductIdRef.current) {
            setOppositeUnitId('');
          }
        } catch (error) {
          console.error('Error fetching existing units:', error);
        }
      };

      fetchExistingUnits();
    }
  }, [product_id, loading]);

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare the data to be sent to the backend
      const updatedUnitData = {
        product_id,
        unit_type,
        unit_category: unit_category || 'buying',
        opposite_unit_id, // Include opposite unit ID
        conversion_rate: parseFloat(conversionRate)
      };

      const response = await axiosInstance.put(`/units/${id}`, updatedUnitData);
    
      if (response.status === 200) {
        // If buying unit, persist suppliers + primary
        if ((unit_category || 'buying') === 'buying') {
          // Validation: if some suppliers selected, enforce a primary within selection
          if (selectedSupplierIds.length > 0 && (!primarySupplierId || !selectedSupplierIds.includes(primarySupplierId))) {
            setSupplierError('Please select a Most Recent Supplier from the selected list.');
            return;
          }
          setSupplierError('');
          try {
            await axiosInstance.put(`/units/${id}/suppliers`, {
              supplier_ids: selectedSupplierIds.map((s) => parseInt(s))
            });
            if (primarySupplierId) {
              await axiosInstance.put(`/units/${id}/primary-supplier`, { supplier_id: parseInt(primarySupplierId) });
            }
          } catch (suppErr) {
            console.warn('Failed to update suppliers/primary:', suppErr);
          }
        }
        setSnackbarMessage('Unit updated successfully. Please note: You will need to reconcile inventory for the changes to reflect accurately.');
        setSnackbarSeverity('warning'); // Set the severity to warning for this alert
        setSnackbarOpen(true);
      }
      
      // Redirect to view page after a brief delay
      setTimeout(() => navigate('/dashboard/units/view'), 3000);

    } catch (error) {
      setSnackbarMessage('Error updating unit: ' + (error.response ? error.response.data.error : error.message));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Helper function to format product display
  const formatProductDisplay = (product) => {
    let display = product.product_name;
    if (product.variety && product.variety !== 'undefined' && product.variety !== 'null') {
      display += ` - ${product.variety}`;
    }
    if (product.brand && product.brand !== 'undefined' && product.brand !== 'null') {
      display += ` (${product.brand})`;
    }
    if (product.size && product.size !== 'undefined' && product.size !== 'null') {
      display += ` [${product.size}]`;
    }
    return display;
  };

  const handleSuppliersChange = (event) => {
    const value = event.target.value || [];

    if (value.includes(ADD_NEW_SUPPLIER_VALUE)) {
      const cleaned = value.filter((v) => v !== ADD_NEW_SUPPLIER_VALUE);
      setSelectedSupplierIds(cleaned);
      setAddSupplierDialogOpen(true);
      return;
    }

    setSelectedSupplierIds(value);
  };

  const handleAddSupplierDialogClose = () => {
    setAddSupplierDialogOpen(false);
  };

  const handleConfirmAddSupplier = () => {
    try {
      const draft = {
        product_id,
        unit_type,
        unit_category,
        opposite_unit_id,
        conversionRate,
        selectedSupplierIds,
        primarySupplierId
      };

      sessionStorage.setItem(EDIT_UNIT_DRAFT_KEY, JSON.stringify(draft));
    } catch (e) {
      console.warn('Failed to save EditUnit draft to sessionStorage', e);
    }

    setAddSupplierDialogOpen(false);
    navigate('/dashboard/suppliers/add', { state: { from: 'edit_unit', unitId: id } });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Edit Unit
        </Typography>
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleFormSubmit}>
                <Grid container spacing={2}>
                  {/* Select Product */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Select Product"
                      variant="outlined"
                      fullWidth
                      value={product_id}
                      onChange={(e) => setProductId(e.target.value)}
                      required
                    >
                      {products.map((product) => (
                        <MenuItem key={product.product_id} value={product.product_id}>
                          {formatProductDisplay(product)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Unit Type */}
                  <Grid item xs={12}>
                    <TextField
                      label="Unit Type"
                      variant="outlined"
                      fullWidth
                      value={unit_type}
                      onChange={(e) => setUnitType(e.target.value)}
                      required
                      inputProps={{ 'data-testid': 'edit-unit-type' }}
                    />
                  </Grid>

                  {/* Dropdown for selecting existing units */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Select Comparison Unit" /*opposite unit*/
                      variant="outlined"
                      fullWidth
                      value={opposite_unit_id}
                      onChange={(e) => setOppositeUnitId(e.target.value)}
                      required
                      disabled={!existingUnits.length}
                    >
                      {existingUnits.map((unit) => (
                        <MenuItem key={unit.unit_id} value={unit.unit_id}>
                          {unit.unit_type} ({unit.unit_category})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Conversion Rate */}
                  <Grid item xs={12}>
                    <TextField
                      label={`How many ${existingUnits.find(unit => unit.unit_id === opposite_unit_id)?.unit_type || 'selected unit type'} are there in ${unit_type || 'current unit type'}?`}
                      variant="outlined"
                      fullWidth
                      value={conversionRate}
                      onChange={(e) => setConversionRate(e.target.value)}
                      required
                      inputProps={{ 'data-testid': 'edit-unit-conversion-rate' }}
                    />
                  </Grid>

                  {/* Suppliers (multiple) and primary selection - buying units only */}
                  {unit_category === 'buying' && (
                    <>
                      <Grid item xs={12}>
                        <FormControl fullWidth error={!!supplierError}>
                          <InputLabel id="suppliers-multi-label">Suppliers (optional, multiple)</InputLabel>
                          <Select
                            labelId="suppliers-multi-label"
                            label="Suppliers (optional, multiple)"
                            multiple
                            value={selectedSupplierIds}
                            onChange={handleSuppliersChange}
                            renderValue={(selected) => {
                              const names = suppliers
                                .filter((s) => selected.includes(String(s.supplier_id)))
                                .map((s) => s.name || s.supplier_name || 'Unnamed');
                              return names.join(', ');
                            }}
                          >
                            {suppliers.map((s) => (
                              <MenuItem key={s.supplier_id} value={String(s.supplier_id)}>
                                {s.name || s.supplier_name || 'Unnamed'}
                              </MenuItem>
                            ))}
                            <MenuItem value={ADD_NEW_SUPPLIER_VALUE}>
                              <em>Add New Supplier...</em>
                            </MenuItem>
                          </Select>
                          {supplierError && <FormHelperText>{supplierError}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Most Recent Supplier</Typography>
                        <RadioGroup
                          value={primarySupplierId}
                          onChange={(e) => setPrimarySupplierId(e.target.value)}
                        >
                          {selectedSupplierIds.length === 0 ? (
                            <FormHelperText>Select at least one supplier to choose a primary.</FormHelperText>
                          ) : (
                            selectedSupplierIds.map((sid) => {
                              const sup = suppliers.find((s) => String(s.supplier_id) === sid);
                              const label = sup ? ((sup.name || sup.supplier_name || 'Unnamed') + (sup.market_name ? ` (${sup.market_name})` : '')) : sid;
                              return (
                                <FormControlLabel key={sid} value={sid} control={<Radio />} label={label} />
                              );
                            })
                          )}
                        </RadioGroup>
                      </Grid>
                    </>
                  )}

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      data-testid="edit-unit-save"
                    >
                      Update Unit
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Snackbar Notification */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog open={addSupplierDialogOpen} onClose={handleAddSupplierDialogClose}>
        <DialogTitle sx={{ textAlign: 'center' }}>Add New Supplier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to add a new supplier now? You will be taken to the Add Supplier page. When you return, your
            current changes on this page will be restored and the new supplier will be selected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={handleAddSupplierDialogClose} autoFocus>
            No
          </Button>
          <Button onClick={handleConfirmAddSupplier} color="primary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditUnit;
